import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import classnames from "classnames";
import { Loader } from "@mantine/core";
import { gql } from "@/__generated__";
import { Button } from "@/components/buttons/button/button";

import styles from "./letter-of-medical-necessity-content.module.scss";

const GET_LETTER_OF_MEDICAL_NECESSITY = gql(`
  query GetLetterOfMedicalNecessityLetter($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      letterGeneration {
        appealLetterGenerationId
        letterBody
        letterReferences
        updatedTimestamp
      }
    }
  }
`);

const GENERATE_PROVIDER_LETTER_OF_MEDICAL_NECESSITY = gql(`
  mutation GenerateProviderLetterOfMedicalNecessity($appealId: ID!) {
    generateProviderLetterOfMedicalNecessity(appealId: $appealId)
  }
`);

const POLL_INTERVAL_MS = 10_000;

interface LetterOfMedicalNecessityContentProps {
  readonly appealId: string;
}

const renderParagraph = (paragraph: string, index: number) => {
  if (!paragraph.trim()) {
    return <br key={index} />;
  }

  if (paragraph.startsWith("**")) {
    return (
      <p
        className={classnames(styles.paragraph, styles.paragraphBold)}
        key={index}
      >
        {paragraph.split("**").join("")}
      </p>
    );
  }

  return (
    <p className={styles.paragraph} key={index}>
      {paragraph}
    </p>
  );
};

export function LetterOfMedicalNecessityContent({
  appealId,
}: LetterOfMedicalNecessityContentProps) {
  const [hasClickedGenerate, setHasClickedGenerate] = useState(false);
  const [initialLetterId, setInitialLetterId] = useState<string | null>(null);
  const [hasCapturedInitial, setHasCapturedInitial] = useState(false);

  const { data, loading, stopPolling, startPolling } = useQuery(
    GET_LETTER_OF_MEDICAL_NECESSITY,
    {
      variables: { appealId },
      fetchPolicy: "no-cache",
    },
  );

  const [
    generateProviderLetterOfMedicalNecessity,
    { loading: startingGeneration },
  ] = useMutation(GENERATE_PROVIDER_LETTER_OF_MEDICAL_NECESSITY);

  const letterGeneration = data?.getAppealById?.letterGeneration;
  const currentLetterId = letterGeneration?.appealLetterGenerationId ?? null;
  const letterBody = letterGeneration?.letterBody ?? null;
  const letterReferences = letterGeneration?.letterReferences ?? null;

  useEffect(() => {
    if (!loading && !hasCapturedInitial) {
      setInitialLetterId(currentLetterId);
      setHasCapturedInitial(true);
    }
  }, [loading, hasCapturedInitial, currentLetterId]);

  useEffect(() => {
    if (!hasClickedGenerate || !hasCapturedInitial) {
      return;
    }

    const hasNewLetter =
      currentLetterId !== null && currentLetterId !== initialLetterId;
    const newLetterIsReady = hasNewLetter && letterBody !== null;

    if (newLetterIsReady) {
      stopPolling();
      setHasClickedGenerate(false);
    }
  }, [
    hasClickedGenerate,
    hasCapturedInitial,
    currentLetterId,
    initialLetterId,
    letterBody,
    stopPolling,
  ]);

  const handleGenerate = async () => {
    await generateProviderLetterOfMedicalNecessity({ variables: { appealId } });
    setHasClickedGenerate(true);
    startPolling(POLL_INTERVAL_MS);
  };

  const showSpinner = hasClickedGenerate;
  const showLetter = !showSpinner && letterBody !== null;
  const showButton = !showSpinner && !showLetter && !loading;

  return (
    <div className={styles.previewContainer}>
      {showButton && (
        <div className={styles.actionRow}>
          <Button
            variant="primary"
            size="medium"
            onClick={handleGenerate}
            loading={startingGeneration}
            data-testid="generate-letter-of-medical-necessity-button"
          >
            Generate Letter of Medical Necessity
          </Button>
        </div>
      )}

      {showSpinner && (
        <div className={styles.loadingState}>
          <Loader size={32} />
          <p>
            Generating Letter of Medical Necessity... this can take 1-3 minutes.
          </p>
        </div>
      )}

      {showLetter && (
        <>
          <p className={classnames(styles.paragraph, styles.paragraphBold)}>
            Letter of Medical Necessity
          </p>
          {letterBody
            .split("\n")
            .map((paragraph, index) => renderParagraph(paragraph, index))}
          {letterReferences && (
            <>
              <p className={styles.referencesHeader}>References</p>
              {letterReferences
                .split("\n")
                .map((paragraph, index) => renderParagraph(paragraph, index))}
            </>
          )}
        </>
      )}
    </div>
  );
}
